import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, ContentWithPaddingXl } from "components/misc/Layouts";
import tw from "twin.macro";
import styled, { css } from "styled-components/macro";
import { PrimaryButton } from "components/misc/Buttons";
import useGetEventDetail from "../data/useGetEventDetail";
import { getAbsoluteUrl } from "../utils/urlUtils";
import Layout from "components/layouts/Layout";
import { formatDistanceToNow, format } from "date-fns";
import { IconArrowLeft } from "@tabler/icons-react";

// Styled components
const Header = tw.div`flex flex-col md:flex-row items-center justify-between mb-8 gap-4`;
const BackButton = tw(PrimaryButton)`flex items-center gap-2 mb-6 md:mb-0`;
const EventTitle = tw.h1`text-3xl md:text-4xl lg:text-5xl font-black text-gray-900`;
const MetaContainer = tw.div`flex flex-wrap gap-4 md:gap-6 mt-6 text-sm md:text-base`;
const MetaItem = tw.div`flex items-center gap-2`;
const MetaLabel = tw.span`font-bold text-primary-500`;
const MetaValue = tw.span`text-gray-600`;

const DescriptionSection = tw.div`mt-8 md:mt-12 p-6 md:p-8 bg-gray-100 rounded-lg`;
const DescriptionTitle = tw.h2`text-2xl font-bold text-gray-900 mb-4`;
const DescriptionText = tw.p`text-gray-700 leading-relaxed text-base md:text-lg`;

const GallerySection = tw.div`mt-12 md:mt-16`;
const GalleryTitle = tw.h2`text-2xl md:text-3xl font-bold text-gray-900 mb-6 md:mb-8`;
const GalleryGrid = tw.div`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6`;

const ImageContainer = styled.div`
  ${tw`relative w-full h-64 sm:h-72 md:h-80 rounded-lg overflow-hidden cursor-pointer bg-gray-200`}
  transition: transform 0.3s ease-in-out;

  &:hover {
    transform: scale(1.02);
  }

  img {
    ${tw`w-full h-full object-cover`}
    transition: transform 0.3s ease-in-out;
  }

  &:hover img {
    transform: scale(1.05);
  }

  &::after {
    content: "";
    ${tw`absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-25 transition-all duration-300`}
  }
`;

const FullscreenOverlay = styled.div`
  ${tw`fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4`}
  animation: fadeIn 0.3s ease-in-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const FullscreenImage = tw.img`max-w-full max-h-[90vh] rounded-lg`;
const CloseButton = tw.button`absolute text-white text-4xl hover:text-gray-300 transition`;
const NavButton = styled.button`
  ${tw`absolute top-1/2 transform -translate-y-1/2 text-white text-3xl hover:text-gray-300 transition px-4 py-2`}
`;

const LoadingContainer = tw.div`w-full py-20 flex items-center justify-center`;
const LoadingText = tw.p`text-lg text-gray-600`;
const ErrorContainer = tw.div`w-full py-20 flex items-center justify-center bg-red-100 rounded-lg`;
const ErrorText = tw.p`text-lg text-red-600`;
const NoImagesContainer = tw.div`w-full py-12 flex items-center justify-center bg-gray-100 rounded-lg`;
const NoImagesText = tw.p`text-lg text-gray-600`;

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [fullscreenImageIndex, setFullscreenImageIndex] = React.useState(null);

  const { data: event, isLoading, isError } = useGetEventDetail(id);

  const handlePrevImage = () => {
    if (event?.eventImages) {
      setFullscreenImageIndex((prev) =>
        prev === 0 ? event.eventImages.length - 1 : prev - 1
      );
    }
  };

  const handleNextImage = () => {
    if (event?.eventImages) {
      setFullscreenImageIndex((prev) =>
        prev === event.eventImages.length - 1 ? 0 : prev + 1
      );
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") setFullscreenImageIndex(null);
    if (e.key === "ArrowLeft") handlePrevImage();
    if (e.key === "ArrowRight") handleNextImage();
  };

  React.useEffect(() => {
    if (fullscreenImageIndex !== null) {
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [fullscreenImageIndex, event?.eventImages?.length]);

  if (isLoading) {
    return (
      <Layout>
        <Container>
          <ContentWithPaddingXl>
            <LoadingContainer>
              <LoadingText>Loading event details...</LoadingText>
            </LoadingContainer>
          </ContentWithPaddingXl>
        </Container>
      </Layout>
    );
  }

  if (isError || !event) {
    return (
      <Layout>
        <Container>
          <ContentWithPaddingXl>
            <ErrorContainer>
              <div tw="text-center">
                <ErrorText>Failed to load event details.</ErrorText>
                <PrimaryButton onClick={() => navigate("/events")} tw="mt-4">
                  Back to Events
                </PrimaryButton>
              </div>
            </ErrorContainer>
          </ContentWithPaddingXl>
        </Container>
      </Layout>
    );
  }

  const eventDate = new Date(event.date);
  const startTime = event.startTime || "N/A";
  const endTime = event.endTime || "N/A";

  return (
    <Layout>
      <Container>
        <ContentWithPaddingXl>
          {/* Back Button */}
          <BackButton onClick={() => navigate("/events")}>
            <IconArrowLeft size={20} />
            Back to Events
          </BackButton>

          {/* Event Title and Meta Information */}
          <div>
            <EventTitle>{event.name}</EventTitle>
            <MetaContainer>
              <MetaItem>
                <MetaLabel>Date:</MetaLabel>
                <MetaValue>{format(eventDate, "MMMM dd, yyyy")}</MetaValue>
              </MetaItem>
              <MetaItem>
                <MetaLabel>Time:</MetaLabel>
                <MetaValue>
                  {startTime} - {endTime}
                </MetaValue>
              </MetaItem>
              <MetaItem>
                <MetaLabel>Posted:</MetaLabel>
                <MetaValue>
                  {formatDistanceToNow(eventDate, { addSuffix: true })}
                </MetaValue>
              </MetaItem>
            </MetaContainer>
          </div>

          {/* Description Section */}
          {event.description && (
            <DescriptionSection>
              <DescriptionTitle>About Event</DescriptionTitle>
              <DescriptionText>{event.description}</DescriptionText>
            </DescriptionSection>
          )}

          {/* Gallery Section */}
          <GallerySection>
            <GalleryTitle>Event Gallery</GalleryTitle>
            {event.eventImages && event.eventImages.length > 0 ? (
              <GalleryGrid>
                {event.eventImages.map((image, index) => (
                  <ImageContainer
                    key={image.id}
                    onClick={() => setFullscreenImageIndex(index)}
                  >
                    <img
                      src={getAbsoluteUrl(image.imageUrl)}
                      alt={`${event.name} - Image ${index + 1}`}
                      loading="lazy"
                    />
                  </ImageContainer>
                ))}
              </GalleryGrid>
            ) : (
              <NoImagesContainer>
                <NoImagesText>No images available for this event.</NoImagesText>
              </NoImagesContainer>
            )}
          </GallerySection>
        </ContentWithPaddingXl>
      </Container>

      {/* Fullscreen Image Viewer */}
      {fullscreenImageIndex !== null && event.eventImages && (
        <FullscreenOverlay
          onClick={() => setFullscreenImageIndex(null)}
          role="dialog"
          aria-label="Image viewer"
        >
          <CloseButton
            onClick={() => setFullscreenImageIndex(null)}
            aria-label="Close image viewer"
          >
            ×
          </CloseButton>

          <NavButton
            onClick={(e) => {
              e.stopPropagation();
              handlePrevImage();
            }}
            tw="left-0"
            aria-label="Previous image"
          >
            ‹
          </NavButton>

          <FullscreenImage
            src={getAbsoluteUrl(
              event.eventImages[fullscreenImageIndex].imageUrl
            )}
            alt={`${event.name} - Full size`}
            onClick={(e) => e.stopPropagation()}
          />

          <NavButton
            onClick={(e) => {
              e.stopPropagation();
              handleNextImage();
            }}
            aria-label="Next image"
          >
            ›
          </NavButton>

          {/* Image Counter */}
          <div tw="absolute  left-1/2 transform -translate-x-1/2 text-white bg-black bg-opacity-50 px-4 py-2 rounded-lg text-sm md:text-base">
            {fullscreenImageIndex + 1} / {event.eventImages.length}
          </div>
        </FullscreenOverlay>
      )}
    </Layout>
  );
}
